import type { Task } from '@medplum/fhirtypes'
import { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { compact } from 'lodash-es'
import { Navigate, type RouteObject } from 'react-router'
import { AdmissionDisplayPage } from '#src/components/admission/AdmissionDisplayPage.tsx'
import { PatientAdmissionsPage } from '#src/components/admission/PatientAdmissionsPage.tsx'
import { AppointmentDisplayPage } from '#src/components/appointment/AppointmentDisplayPage.tsx'
import { PatientAppointmentsPage } from '#src/components/appointment/PatientAppointmentsPage.tsx'
import { CarePlanDisplayPage } from '#src/components/care-plan/CarePlanDisplayPage.tsx'
import { CarePlanRootPage } from '#src/components/care-plan/CarePlanRootPage.tsx'
import { CarePlanSnapshotPage } from '#src/components/care-plan/CarePlanSnapshotPage.tsx'
import { CarePlanHistoryPage } from '#src/components/care-plan/history/CarePlanHistoryPage.tsx'
import { CarePlanSignaturesPage } from '#src/components/care-plan/signatures/CarePlanSignaturesPage.tsx'
import { CarePlanSnapshotSignaturesPage } from '#src/components/care-plan/signatures/CarePlanSnapshotSignaturesPage.tsx'
import {
  BaseBreadcrumb,
  ResourceBreadcrumb,
} from '#src/components/core/Breadcrumbs.tsx'
import { CoverageDisplayPage } from '#src/components/coverage/CoverageDisplayPage.tsx'
import { PatientCoveragePage } from '#src/components/coverage/PatientCoveragePage.tsx'
import { DocumentReferencePage } from '#src/components/document-reference/DocumentReferencePage.tsx'
import { PatientDocumentsPage } from '#src/components/document-reference/PatientDocumentsPage.tsx'
import { MedicationDetailPage } from '#src/components/medications/MedicationDetailPage.tsx'
import { PatientMedicationsPage } from '#src/components/medications/MedicationsPage.tsx'
import { OrderPage } from '#src/components/order/OrderPage.tsx'
import { PatientOrdersPage } from '#src/components/order/PatientOrdersPage.tsx'
import { ImmunizationDetail } from '#src/components/patient/immunization/ImmunizationDetail.tsx'
import { PatientImmunizationsPage } from '#src/components/patient/immunization/PatientImmunizationsPage.tsx'
import { SDRDisplayPage } from '#src/components/sdr/SDRDisplayPage.tsx'
import { SDRsPatientPage } from '#src/components/sdr/SDRsPatientPage.tsx'
import { ParticipantAllergyPage } from '#src/pages/participants/allergies/ParticipantAllergyPage.tsx'
import { ParticipantConditionPage } from '#src/pages/participants/conditions/ParticipantConditionPage.tsx'
import { ParticipantConditionsPage } from '#src/pages/participants/conditions/ParticipantConditionsPage.tsx'
import { ParticipantDeskPage } from '#src/pages/participants/desks/ParticipantDeskPage.tsx'
import { ParticipantDesksPage } from '#src/pages/participants/desks/ParticipantDesksPage.tsx'
import {
  ParticipantAssessmentPage,
  ParticipantIncidentPage,
  ParticipantIncidentsPage,
  ParticipantTasksPage,
} from '#src/pages/participants/index.ts'
import { MemoriesPage } from '#src/pages/participants/MemoriesPage.tsx'
import { ParticipantObservationPage } from '#src/pages/participants/observations/ParticipantObservationPage.tsx'
import { ParticipantObservationsPage } from '#src/pages/participants/observations/ParticipantObservationsPage.tsx'
import { ParticipantTimelinePage } from '#src/pages/participants/timeline/ParticipantTimelinePage.tsx'
import { ValidatedToolPage } from '#src/pages/participants/validated-tools/ValidatedToolPage.tsx'
import { ValidatedToolResponsePage } from '#src/pages/participants/validated-tools/ValidatedToolResponsePage.tsx'
import { ValidatedToolsPage } from '#src/pages/participants/validated-tools/ValidatedToolsPage.tsx'
import { ParticipantVisitNotePage } from '#src/pages/participants/visit-notes/ParticipantVisitNotePage.tsx'
import { ParticipantVisitNotesPage } from '#src/pages/participants/visit-notes/ParticipantVisitNotesPage.tsx'
import { TaskPage } from '#src/pages/task/TaskPage.tsx'
import { createLoader } from '#src/routes/utils.ts'

export function getPatientRoutes(): RouteObject[] {
  return compact([
    { index: true, element: <ParticipantTimelinePage /> },
    {
      path: '',
      element: <ParticipantTimelinePage />,
    },
    {
      path: 'appointments',
      handle: {
        crumb: () => <BaseBreadcrumb to="appointments" label="Appointments" />,
      },
      children: [
        { index: true, element: <PatientAppointmentsPage /> },
        {
          path: ':appointmentId',
          element: <AppointmentDisplayPage />,
          loader: createLoader('appointmentId'),
          handle: {
            crumb: ({ appointmentId }: { appointmentId: string }) => (
              <ResourceBreadcrumb
                to={`appointments/${appointmentId}`}
                resourceType="Appointment"
                resourceId={appointmentId}
              />
            ),
          },
        },
      ],
    },
    {
      path: 'medications',
      handle: {
        crumb: () => <BaseBreadcrumb to="medications" label="Medications" />,
      },
      children: [
        { index: true, element: <PatientMedicationsPage /> },
        {
          path: ':medicationId',
          element: <MedicationDetailPage />,
          loader: createLoader('medicationId'),
          handle: {
            crumb: ({ medicationId }: { medicationId: string }) => (
              <ResourceBreadcrumb
                to={`medications/${medicationId}`}
                resourceType="MedicationRequest"
                resourceId={medicationId}
              />
            ),
          },
        },
      ],
    },
    {
      path: 'immunizations',
      handle: {
        crumb: () => (
          <BaseBreadcrumb to="immunizations" label="Immunizations" />
        ),
      },
      children: [
        { index: true, element: <PatientImmunizationsPage /> },
        {
          path: ':immunizationId',
          element: <ImmunizationDetail />,
          loader: createLoader('immunizationId'),
          handle: {
            crumb: ({ immunizationId }: { immunizationId: string }) => (
              <ResourceBreadcrumb
                to={`immunizations/${immunizationId}`}
                resourceType="Immunization"
                resourceId={immunizationId}
              />
            ),
          },
        },
      ],
    },
    {
      path: 'tasks',
      handle: { crumb: () => <BaseBreadcrumb to="tasks" label="Task" /> },
      children: [
        { index: true, element: <ParticipantTasksPage /> },
        {
          path: ':taskId',
          element: <TaskPage />,
          loader: createLoader('taskId'),
          handle: {
            crumb: ({ taskId }: { taskId: string }) => (
              <ResourceBreadcrumb
                to={`tasks/${taskId}`}
                resourceType="Task"
                resourceId={taskId}
              />
            ),
          },
        },
      ],
    },
    {
      path: 'orders',
      handle: { crumb: () => <BaseBreadcrumb to="orders" label="Order" /> },
      children: [
        { index: true, element: <PatientOrdersPage /> },
        { path: 'new', element: <PatientOrdersPage /> },
        { path: ':orderId', element: <OrderPage hideMaximize /> },
      ],
    },
    {
      path: 'sdr',
      handle: { crumb: () => <BaseBreadcrumb to="sdr" label="SDR" /> },
      children: [
        { index: true, element: <SDRsPatientPage /> },
        {
          path: ':taskId/*',
          element: <SDRDisplayPage />,
          loader: createLoader('taskId'),
          handle: {
            crumb: ({ taskId }: { taskId: string }) => (
              <ResourceBreadcrumb
                to={`sdr/${taskId}`}
                resourceType="Task"
                resourceId={taskId}
                getLabel={(task: Task) => {
                  const sdr = SDR.fromFhirResource(task)
                  return sdr.title ?? task.description ?? 'SDR'
                }}
              />
            ),
          },
        },
      ],
    },
    {
      path: 'assessments',
      children: [
        // The standalone assessments list now lives in the consolidated Notes
        // table, so redirect stale list deep links there. Detail links
        // (assessments/:assessmentId) still open the assessment editor below.
        { index: true, element: <Navigate to="../notes/all" replace /> },
        {
          path: ':assessmentId',
          element: <ParticipantAssessmentPage />,
          loader: createLoader('assessmentId'),
        },
      ],
    },
    {
      path: 'incidents',
      handle: {
        crumb: () => <BaseBreadcrumb to="incidents" label="Incidents" />,
      },
      children: [
        { index: true, element: <ParticipantIncidentsPage /> },
        {
          path: ':incidentId',
          element: <ParticipantIncidentPage />,
          loader: createLoader('incidentId'),
        },
      ],
    },
    {
      path: 'validated-tools',
      handle: {
        crumb: () => (
          <BaseBreadcrumb to="validated-tools" label="Validated Tools" />
        ),
      },
      children: [
        { index: true, element: <ValidatedToolsPage /> },
        {
          path: ':validatedToolName',
          element: <ValidatedToolPage />,
          loader: createLoader('validatedToolName'),
          handle: {
            crumb: ({ validatedToolName }: { validatedToolName: string }) => (
              <BaseBreadcrumb
                to={`validated-tools/${validatedToolName}`}
                label="Add Validated Tool"
              />
            ),
          },
        },
        {
          path: 'responses/:questionnaireResponseId',
          element: <ValidatedToolResponsePage />,
          loader: createLoader('questionnaireResponseId'),
          handle: {
            crumb: ({
              questionnaireResponseId,
            }: {
              questionnaireResponseId: string
            }) => (
              <BaseBreadcrumb
                to={`validated-tools/responses/${questionnaireResponseId}`}
                label="Response"
              />
            ),
          },
        },
      ],
    },
    {
      // The notes list now lives under `notes/<view>`; the individual-note
      // detail route stays here, unchanged.
      path: 'visit-notes',
      children: [
        { index: true, element: <Navigate to="../notes/all" replace /> },
        {
          path: ':visitNoteId',
          element: <ParticipantVisitNotePage />,
        },
      ],
    },
    {
      path: 'notes',
      children: [
        { index: true, element: <Navigate to="all" replace /> },
        // One `:view` route (all|mine|assessments) — not three — so toggling the
        // picker keeps the page mounted (no remount/flash); the page reads the
        // view segment.
        { path: ':view', element: <ParticipantVisitNotesPage /> },
      ],
    },
    {
      path: 'care-plans',
      children: [
        {
          index: true,
          element: <CarePlanRootPage />,
        },
        {
          path: ':carePlanId',
          loader: createLoader('carePlanId'),
          handle: {
            crumb: ({ carePlanId }: { carePlanId: string }) => (
              <ResourceBreadcrumb
                to={`care-plans/${carePlanId}`}
                resourceType="CarePlan"
                resourceId={carePlanId}
              />
            ),
          },
          children: [
            { index: true, element: <CarePlanDisplayPage /> },
            { path: '*', element: <CarePlanDisplayPage /> },
            {
              path: 'signatures',
              element: <CarePlanSignaturesPage />,
              handle: {
                crumb: () => (
                  <BaseBreadcrumb to="signatures" label="Signatures" />
                ),
              },
            },
            {
              path: 'interventions/:interventionId',
              element: <CarePlanDisplayPage />,
            },
          ],
        },
        {
          path: 'history',
          handle: {
            crumb: () => (
              <BaseBreadcrumb to="care-plans/history" label="History" />
            ),
          },
          children: [
            { index: true, element: <CarePlanHistoryPage /> },
            {
              path: ':carePlanSnapshotId',
              loader: createLoader('carePlanSnapshotId'),
              handle: {
                crumb: ({
                  carePlanSnapshotId,
                }: {
                  carePlanSnapshotId: string
                }) => (
                  <ResourceBreadcrumb
                    to={`care-plans/history/${carePlanSnapshotId}`}
                    resourceType="Composition"
                    resourceId={carePlanSnapshotId}
                  />
                ),
              },
              children: [
                { index: true, element: <CarePlanSnapshotPage /> },
                { path: '*', element: <CarePlanSnapshotPage /> },
                {
                  path: 'signatures',
                  element: <CarePlanSnapshotSignaturesPage />,
                  handle: {
                    crumb: () => (
                      <BaseBreadcrumb to="signatures" label="Signatures" />
                    ),
                  },
                },
                {
                  path: 'interventions/:interventionId',
                  element: <CarePlanSnapshotPage />,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: 'desks',
      handle: {
        crumb: () => <BaseBreadcrumb to="desks" label="Desks" />,
      },
      children: [
        { index: true, element: <ParticipantDesksPage /> },
        {
          path: ':deskId',
          element: <ParticipantDeskPage />,
        },
      ],
    },
    {
      path: 'documents',
      handle: {
        crumb: () => <BaseBreadcrumb to="documents" label="Documents" />,
      },
      children: [
        { index: true, element: <PatientDocumentsPage /> },
        {
          path: ':documentId',
          element: <DocumentReferencePage />,
          loader: createLoader('documentId'),
          handle: {
            crumb: ({ documentId }: { documentId: string }) => (
              <ResourceBreadcrumb
                to={`documents/${documentId}`}
                resourceType="DocumentReference"
                resourceId={documentId}
              />
            ),
          },
        },
      ],
    },
    {
      path: 'allergies',
      handle: {
        crumb: () => <BaseBreadcrumb to="allergies" label="Allergies" />,
      },
      children: [
        {
          path: ':allergyId',
          element: <ParticipantAllergyPage />,
          loader: createLoader('allergyId'),
          handle: {
            crumb: ({ allergyId }: { allergyId: string }) => (
              <ResourceBreadcrumb
                to={`allergies/${allergyId}`}
                resourceType="AllergyIntolerance"
                resourceId={allergyId}
              />
            ),
          },
        },
      ],
    },
    {
      path: 'conditions',
      handle: {
        crumb: () => <BaseBreadcrumb to="conditions" label="Conditions" />,
      },
      children: [
        { index: true, element: <ParticipantConditionsPage /> },
        {
          path: ':conditionId',
          element: <ParticipantConditionPage />,
          loader: createLoader('conditionId'),
          handle: {
            crumb: ({ conditionId }: { conditionId: string }) => (
              <ResourceBreadcrumb
                to={`conditions/${conditionId}`}
                resourceType="Condition"
                resourceId={conditionId}
              />
            ),
          },
        },
      ],
    },
    {
      path: 'observations',
      handle: {
        crumb: () => <BaseBreadcrumb to="observations" label="Observations" />,
      },
      children: [
        { index: true, element: <ParticipantObservationsPage /> },
        {
          path: ':observationId',
          element: <ParticipantObservationPage />,
          loader: createLoader('observationId'),
          handle: {
            crumb: ({ observationId }: { observationId: string }) => (
              <ResourceBreadcrumb
                to={`observations/${observationId}`}
                resourceType="Observation"
                resourceId={observationId}
              />
            ),
          },
        },
      ],
    },
    {
      path: 'coverage',
      handle: {
        crumb: () => <BaseBreadcrumb to="coverage" label="Coverage" />,
      },
      children: [
        { index: true, element: <PatientCoveragePage /> },
        {
          path: ':coverageId',
          element: <CoverageDisplayPage />,
          loader: createLoader('coverageId'),
          handle: {
            crumb: ({ coverageId }: { coverageId: string }) => (
              <ResourceBreadcrumb
                to={`coverage/${coverageId}`}
                resourceType="Coverage"
                resourceId={coverageId}
              />
            ),
          },
        },
      ],
    },
    {
      path: 'admissions',
      handle: {
        crumb: () => <BaseBreadcrumb to="admissions" label="Admissions" />,
      },
      children: [
        { index: true, element: <PatientAdmissionsPage /> },
        {
          path: ':encounterId',
          element: <AdmissionDisplayPage />,
          loader: createLoader('encounterId'),
          handle: {
            crumb: ({ encounterId }: { encounterId: string }) => (
              <ResourceBreadcrumb
                to={`admissions/${encounterId}`}
                resourceType="Encounter"
                resourceId={encounterId}
              />
            ),
          },
        },
      ],
    },
    {
      path: 'immunizations/:immunizationId',
      element: <ImmunizationDetail />,
      loader: createLoader('immunizationId'),
      handle: {
        crumb: ({ immunizationId }: { immunizationId: string }) => (
          <ResourceBreadcrumb
            to={`immunizations/${immunizationId}`}
            resourceType="Immunization"
            resourceId={immunizationId}
          />
        ),
      },
    },
    {
      path: 'memories',
      handle: {
        crumb: () => <BaseBreadcrumb to="memories" label="Memories" />,
      },
      element: <MemoriesPage />,
    },
  ])
}
